import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { ChevronRight, FolderPlus, FolderUp, Grid3X3, List, Share2, Sparkles, Upload, UploadCloud } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { entriesFromDrop, entriesFromInput, uploadFiles, uploadTree } from "@/lib/upload";
import { useMe } from "@/context/MeContext";
import { useFiles } from "@/context/FilesContext";
import { useCompany } from "@/context/CompanyContext";
import { FileBrowser } from "@/components/files/FileBrowser";
import { NameDialog } from "@/components/files/NameDialog";
import { PermissionsDialog } from "@/components/files/PermissionsDialog";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";

export default function Explorer() {
  const { folderId } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { me } = useMe();
  const { company } = useCompany();
  const allSpaces = !folderId && me.role === "admin" && searchParams.get("espace") === "tous";
  const rootLabel = `${allSpaces ? "Tous les espaces" : "Mes fichiers"} · ${company?.name || ""}`;
  const { refreshKey } = useFiles();
  const [data, setData] = useState(null);
  const [view, setView] = useState(() => localStorage.getItem("sdr-view") || "list");
  const [newFolder, setNewFolder] = useState(false);
  const [permOpen, setPermOpen] = useState(false);
  const [drag, setDrag] = useState(false);
  const [autoClassify, setAutoClassify] = useState(() => localStorage.getItem("sdr-auto-classify") !== "false");
  const input = useRef(null);
  const dirInput = useRef(null);
  useEffect(() => { localStorage.setItem("sdr-auto-classify", autoClassify ? "true" : "false"); }, [autoClassify]);

  const load = useCallback(() => {
    api.get("/explorer", { params: { folder_id: folderId, scope: allSpaces ? "all" : undefined } }).then((r) => setData(r.data))
      .catch((e) => { toast.error(errMsg(e)); navigate("/fichiers"); });
  }, [folderId, allSpaces, navigate]);
  useEffect(() => { load(); }, [load, refreshKey]);
  useEffect(() => { localStorage.setItem("sdr-view", view); }, [view]);

  const canWrite = data && (folderId ? ["edit", "owner"].includes(data.access) : me.role !== "externe");
  useEffect(() => { window.addEventListener("sdr-uploads-finished", load); return () => window.removeEventListener("sdr-uploads-finished", load); }, [load]);
  const doUpload = (files) => uploadFiles(files, folderId, { autoClassify: !!folderId || autoClassify });
  const doTree = (items) => uploadTree(items, folderId);
  const onDrop = async (e) => {
    e.preventDefault(); setDrag(false);
    if (!canWrite) return;
    const files = Array.from(e.dataTransfer.files || []);
    const tree = entriesFromDrop(e.dataTransfer);
    const items = await tree;
    if (items) doTree(items);
    else if (files.length) doUpload(files);
  };

  return (
    <div className="max-w-7xl mx-auto fade-up" onDragOver={(e) => { e.preventDefault(); canWrite && setDrag(true); }}
      onDragLeave={(e) => e.currentTarget === e.target && setDrag(false)} onDrop={onDrop} data-testid="explorer-page">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-6">
        <div className="min-w-0">
          <nav className="flex items-center gap-1 text-sm text-slate-500 flex-wrap" data-testid="breadcrumbs">
            {data?.folder && data.breadcrumbs[0] && data.folder.owner_id !== me.user_id
              ? (me.role === "admin"
                ? <Link to="/fichiers?espace=tous" className="hover:text-[#1E3A8A]" data-testid="breadcrumb-root">Tous les espaces</Link>
                : <Link to="/partages" className="hover:text-[#1E3A8A]" data-testid="breadcrumb-root">Partagés avec moi</Link>)
              : <Link to="/fichiers" className="hover:text-[#1E3A8A]" data-testid="breadcrumb-root">{folderId ? `Mes fichiers · ${company?.name || ""}` : rootLabel}</Link>}
            {data?.breadcrumbs.map((b) => (
              <span key={b.id} className="flex items-center gap-1"><ChevronRight className="h-3.5 w-3.5" />
                <Link to={`/fichiers/${b.id}`} className="hover:text-[#1E3A8A]" data-testid={`breadcrumb-${b.id}`}>{b.name}</Link></span>
            ))}
          </nav>
          <h1 className="font-display text-2xl sm:text-3xl font-bold tracking-tight mt-1 truncate" data-testid="explorer-title">
            {data?.folder?.name || (allSpaces ? "Tous les espaces" : "Mes fichiers")}
          </h1>
          {!folderId && me.role === "admin" && (
            <div className="mt-3 inline-flex rounded-md border bg-white p-0.5 text-sm" data-testid="explorer-scope-toggle">
              <Link to="/fichiers" className={`px-3 py-1 rounded ${!allSpaces ? "bg-[#1E3A8A] text-white" : "text-slate-600"}`} data-testid="scope-mine">Mes fichiers</Link>
              <Link to="/fichiers?espace=tous" className={`px-3 py-1 rounded ${allSpaces ? "bg-[#1E3A8A] text-white" : "text-slate-600"}`} data-testid="scope-all">Tous les espaces</Link>
            </div>
          )}
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {data?.folder?.access === "owner" && me.role !== "externe" && (
            <Button variant="outline" onClick={() => setPermOpen(true)} data-testid="folder-share-btn"><Share2 className="h-4 w-4 mr-2" />Partager</Button>
          )}
          {folderId && canWrite && (
            <Button variant="outline" onClick={async () => {
              try { const { data: r } = await api.post(`/ai/folders/${folderId}/analyze`); toast.success(r.started ? `Analyse lancée pour ${r.started} document(s)` : "Tous les documents de ce dossier sont déjà analysés"); }
              catch (e) { toast.error(errMsg(e)); }
            }} data-testid="explorer-analyze-folder-btn"><Sparkles className="h-4 w-4 mr-2" />Analyser ce dossier</Button>
          )}
          {!folderId && me.role === "admin" && data?.files?.length > 0 && (
            <Button variant="outline" onClick={async () => {
              try { const { data: r } = await api.post("/ai/auto-classify-all"); toast.success(`Rangement lancé pour ${r.started} document(s), actualisez dans une minute`); setTimeout(load, 30000); }
              catch (e) { toast.error(errMsg(e)); }
            }} data-testid="explorer-classify-root-btn"><Sparkles className="h-4 w-4 mr-2" />Ranger la racine maintenant</Button>
          )}
          {canWrite && <>
            {!folderId && (
              <label className="flex items-center gap-2 text-sm text-slate-700 bg-white border rounded-md px-3 h-9 cursor-pointer select-none"
                title="Cochée : l'IA range le document dans le bon dossier (et la bonne société). Décochée : il reste où vous le déposez."
                data-testid="upload-auto-classify-label">
                <Checkbox checked={autoClassify} onCheckedChange={(v) => setAutoClassify(!!v)} data-testid="upload-auto-classify-checkbox" />
                <Sparkles className="h-4 w-4 text-[#1E3A8A]" />Ranger avec l'IA
              </label>
            )}
            <Button variant="outline" onClick={() => setNewFolder(true)} data-testid="new-folder-btn"><FolderPlus className="h-4 w-4 mr-2" />Nouveau dossier</Button>
            <Button variant="outline" onClick={() => dirInput.current?.click()} data-testid="upload-folder-btn"><FolderUp className="h-4 w-4 mr-2" />Déposer un dossier</Button>
            <input ref={dirInput} type="file" multiple hidden webkitdirectory="" directory=""
              onChange={(e) => { doTree(entriesFromInput(e.target.files)); e.target.value = ""; }} data-testid="upload-folder-input" />
            <Button onClick={() => input.current?.click()} data-testid="upload-btn"><Upload className="h-4 w-4 mr-2" />Téléverser</Button>
            <input ref={input} type="file" multiple hidden onChange={(e) => { doUpload(e.target.files); e.target.value = ""; }} data-testid="upload-input" />
          </>}
          <div className="flex border rounded-md bg-white">
            <Button variant="ghost" size="icon" className={view === "list" ? "bg-slate-100" : ""} onClick={() => setView("list")} aria-label="Vue liste" data-testid="view-list-btn"><List className="h-4 w-4" /></Button>
            <Button variant="ghost" size="icon" className={view === "grid" ? "bg-slate-100" : ""} onClick={() => setView("grid")} aria-label="Vue grille" data-testid="view-grid-btn"><Grid3X3 className="h-4 w-4" /></Button>
          </div>
        </div>
      </div>
      <div className={`relative rounded-xl transition-colors ${drag ? "ring-2 ring-[#1E3A8A] ring-offset-4 bg-blue-50/50" : ""}`}>
        {drag && <div className="absolute inset-0 z-10 flex items-center justify-center pointer-events-none">
          <div className="bg-white shadow-lg rounded-xl px-6 py-4 flex items-center gap-3 text-[#1E3A8A] font-semibold"><UploadCloud className="h-6 w-6" />Déposez vos fichiers ici</div></div>}
        {data && <FileBrowser folders={data.folders} files={data.files} view={view} onChanged={load} showOwner={allSpaces}
          emptyText={canWrite ? "Ce dossier est vide — glissez-déposez des fichiers ou utilisez « Téléverser »." : "Ce dossier est vide"} />}
      </div>
      <NameDialog open={newFolder} onOpenChange={setNewFolder} title="Nouveau dossier" confirmLabel="Créer" testId="new-folder-dialog"
        onSubmit={async (name) => {
          try { await api.post("/folders", { name, parent_id: folderId || null }); toast.success("Dossier créé"); load(); }
          catch (e) { toast.error(errMsg(e)); throw e; }
        }} />
      <PermissionsDialog open={permOpen} onOpenChange={setPermOpen} item={data?.folder} kind="folders" onSaved={load} />
    </div>
  );
}
